import { useEffect, useCallback, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@vc/lib/utils";

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
}

export function BottomSheet({ 
  open, 
  onClose, 
  title,
  children, 
  className 
}: BottomSheetProps) {
  // Mounted/visible state for exit animation
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Swipe-to-dismiss state
  const touchStartYRef = useRef(0);
  const touchCurrentYRef = useRef(0);

  useEffect(() => {
    if (open) {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      setMounted(true);
      // Small rAF delay so the enter animation triggers after mount
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
      closeTimerRef.current = setTimeout(() => setMounted(false), 320);
    }
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [open]);

  // Handle escape key
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && open) {
      onClose();
    }
  }, [open, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  // Prevent body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Swipe gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0].clientY;
    touchCurrentYRef.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchCurrentYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    const diff = touchCurrentYRef.current - touchStartYRef.current;
    if (diff > 80) {
      onClose();
    }
  };

  if (!mounted) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className={cn(
          "absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300",
          visible ? "opacity-100" : "opacity-0"
        )}
        onClick={onClose}
      />
      
      {/* Bottom Sheet */}
      <div 
        className={cn(
          "absolute bottom-0 left-0 right-0 rounded-t-3xl bg-pannelcolor shadow-2xl",
          "transition-transform duration-300 ease-out flex flex-col",
          className
        )}
        style={{ 
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          // height: 'min(82vh, calc(100vh - 60px))'
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Handle bar */}
        <div className="flex items-center justify-center pt-3 pb-1 shrink-0">
          <div className="h-1.5 w-12 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        {title && (
          <div className="flex pb-1 items-center justify-between px-6 border-b border-gray-100 dark:border-border shrink-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-foreground">{title}</h3>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-muted transition"
            >
              <X className="h-5 w-5 text-gray-600 dark:text-foreground" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-3">
          {children}
        </div>

        {/* Safe area spacer for iOS home indicator */}
        <div style={{ height: 'env(safe-area-inset-bottom)' }} />
      </div>
    </div>,
    document.body
  );
}
